import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as api from '../api/client';

const ACTIVE_ACCOUNT_KEY = 'loopcast_active_account_id';

interface InstagramAccountContextValue {
  accounts: api.InstagramAccount[];
  activeAccount: api.InstagramAccount | null;
  setActiveAccountId: (id: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
}

const InstagramAccountContext = createContext<InstagramAccountContextValue | undefined>(undefined);

export function InstagramAccountProvider({ children }: { children: React.ReactNode }) {
  const [accounts, setAccounts] = useState<api.InstagramAccount[]>([]);
  const [activeAccountId, setActiveAccountIdState] = useState<string>(() => localStorage.getItem(ACTIVE_ACCOUNT_KEY) ?? '');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listInstagramAccounts();
      setAccounts(list);
      setActiveAccountIdState((current) => {
        if (current && list.some((a) => a.id === current)) return current;
        return list[0]?.id ?? '';
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await api.listInstagramAccounts();
        if (cancelled) return;
        setAccounts(list);
        setActiveAccountIdState((current) => {
          if (current && list.some((a) => a.id === current)) return current;
          return list[0]?.id ?? '';
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setActiveAccountId = useCallback((id: string) => {
    setActiveAccountIdState(id);
    localStorage.setItem(ACTIVE_ACCOUNT_KEY, id);
  }, []);

  const activeAccount = useMemo(
    () => accounts.find((a) => a.id === activeAccountId) ?? null,
    [accounts, activeAccountId]
  );

  const value = useMemo(
    () => ({ accounts, activeAccount, setActiveAccountId, loading, refresh }),
    [accounts, activeAccount, setActiveAccountId, loading, refresh]
  );

  return <InstagramAccountContext.Provider value={value}>{children}</InstagramAccountContext.Provider>;
}

export function useInstagramAccounts(): InstagramAccountContextValue {
  const ctx = useContext(InstagramAccountContext);
  if (!ctx) throw new Error('useInstagramAccounts must be used within InstagramAccountProvider');
  return ctx;
}
